import ReactDOM from 'react-dom';
import { Loading } from 'react-style-guide';
import { WithTranslationsProps } from 'react-utilities';
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { GameGrid } from '../common/components/GameGrid';
import { TBuildEventProperties } from '../common/components/GameTileUtils';
import bedev1Services from '../common/services/bedev1Services';
import { TMetaData, TGameData, TSort } from '../common/types/bedev1Types';
import {
  newGrid,
  useGameImpressionGridTracker,
  TBuildGridGameImpressionsEventProperties
} from '../common/hooks/useGameImpressionGridTracker';
import { common } from '../common/constants/configConstants';
import { pageContext } from '../common/constants/eventStreamConstants';

type TSortDetailGridProps = {
  sort: TSort;
  translate: WithTranslationsProps['translate'];
};

export const SortDetailGrid = ({ sort, translate }: TSortDetailGridProps): JSX.Element => {
  const gridRef = useRef<HTMLDivElement>(null);
  const tileRef = useRef<HTMLDivElement>(null);
  const [pageCount, setPageCount] = useState<number>(0);
  const [metaData, setMetaData] = useState<TMetaData | Record<string, never>>({});
  const [gameData, setGameData] = useState<TGameData[] | undefined>(undefined);
  const [loading, setLoading] = useState<boolean>(true);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const { games, metaData: listMetaData } = await bedev1Services.getGameSortDetailList(
        sort.token,
        sort.contextCountryRegionId,
        pageCount,
        common.numberOfGameTilesPerLoad,
        sort.gameSetTargetId,
        metaData.nextPageExclusiveStartId
      );
      ReactDOM.unstable_batchedUpdates(() => {
        setGameData([...(gameData || []), ...games]);
        setPageCount(count => count + common.numberOfGameTilesPerLoad);
        setMetaData(listMetaData);
      });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(err);
    }
    setLoading(false);
  }, [pageCount, metaData, gameData, sort]);

  const onScroll = useCallback(() => {
    const gridOffset = (gridRef.current?.offsetTop || 0) + (gridRef.current?.clientHeight || 0);
    const windowOffset = window.pageYOffset + window.innerHeight;
    if (windowOffset > gridOffset && metaData?.hasMoreRows === false) {
      window.removeEventListener('scroll', onScroll);
    } else if (windowOffset > gridOffset && metaData?.hasMoreRows === true && loading === false) {
      // eslint-disable-next-line no-void
      void loadData();
    }
  }, [metaData?.hasMoreRows, loadData, loading]);

  const buildEventProperties: TBuildEventProperties = (data, id) => ({
    placeId: data.placeId,
    universeId: data.universeId,
    isAd: data.isSponsored,
    position: id,
    numberOfLoadedTiles: (gameData || []).length,
    gameSetTypeId: sort.gameSetTypeId,
    gameSetTargetId: sort.gameSetTargetId,
    page: pageContext.sortDetailPageDiscover
  });

  const buildGameImpressionsProperties: TBuildGridGameImpressionsEventProperties = useCallback(
    (viewedIndex: number[]) => {
      if (gameData !== undefined) {
        const parsedViewedIndex = viewedIndex.filter(id => id < gameData?.length);
        return {
          rootPlaceIds: parsedViewedIndex.map(id => gameData[id].placeId),
          universeIds: parsedViewedIndex.map(id => gameData[id].universeId),
          absPositions: parsedViewedIndex,
          adsPositions: parsedViewedIndex.map(id => (gameData[id].isSponsored ? 1 : 0)),
          emphasisFlag: false,
          page: pageContext.sortDetailPageDiscover
        };
      }

      return undefined;
    },
    [gameData]
  );

  useGameImpressionGridTracker(
    tileRef,
    false,
    buildGameImpressionsProperties,
    newGrid.calculateViewedGameData
  );

  useEffect(() => {
    window.addEventListener('scroll', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
    };
  }, [onScroll, sort]);

  useEffect(() => {
    setGameData(undefined);
    // eslint-disable-next-line no-void
    void loadData();
  }, [sort]);

  return (
    <React.Fragment>
      <GameGrid
        ref={gridRef}
        emphasis={false}
        tileRef={tileRef}
        gameData={gameData || []}
        translate={translate}
        buildEventProperties={buildEventProperties}
      />
      {loading && <Loading />}
    </React.Fragment>
  );
};

export default SortDetailGrid;
